import { useState, useRef, useCallback, useEffect } from "react";
import { Artist, Quest, QuestPresets } from "@/mocks/artistData";
import IPImageGenerator, { GeneratedIPImage } from "@/pages/quests/create/components/IPImageGenerator";

// ─── Types ────────────────────────────────────────────────────────────────────
interface PromptBuilderProps {
  quest: Quest;
  artist: Artist;
  accentColor: string;
  secondaryColor: string;
  prompt: string;
  onPromptChange: (prompt: string) => void;
  negativePrompt: string;
  onNegativePromptChange: (v: string) => void;
  selectedTool: string;
  onToolChange: (tool: string) => void;
  onIPImagesChange?: (images: GeneratedIPImage[]) => void;
  onTokenSpend?: (amount: number) => void;
}

// ─── Static data ──────────────────────────────────────────────────────────────
const aiTools = [
  { id: "seedance", name: "Seedance", desc: "ByteDance의 최신 AI 영상 생성 모델", icon: "ri-seedling-line", badge: "추천", color: "#00f5d4" },
  { id: "runway",   name: "Runway Gen-3", desc: "고품질 영상 생성 및 편집 특화",    icon: "ri-film-line",       badge: "인기",   color: "#f72585" },
  { id: "pika",     name: "Pika 2.0",     desc: "빠른 생성 속도와 직관적인 UI",     icon: "ri-flashlight-line", badge: "빠름",   color: "#ffd60a" },
  { id: "kling",    name: "Kling AI",     desc: "한국어 프롬프트 지원, 고해상도",   icon: "ri-sparkling-line",  badge: "한국어", color: "#c77dff" },
  { id: "sora",     name: "Sora",         desc: "OpenAI의 영상 생성 모델, 최고 품질", icon: "ri-openai-line",   badge: "고품질", color: "#ff6b35" },
];

const cameraAnglePresets = [
  { id: "eye-level",   label: "아이 레벨",  icon: "ri-eye-line",             desc: "자연스러운 시점" },
  { id: "low-angle",   label: "로우 앵글",  icon: "ri-arrow-down-line",      desc: "웅장하고 강렬한 느낌" },
  { id: "high-angle",  label: "하이 앵글",  icon: "ri-arrow-up-line",        desc: "내려다보는 시점" },
  { id: "birds-eye",   label: "버즈 아이",  icon: "ri-flight-takeoff-line",  desc: "정수리 위에서 내려다봄" },
  { id: "dutch-angle", label: "더치 앵글",  icon: "ri-contrast-2-line",      desc: "기울어진 긴장감" },
  { id: "pov",         label: "POV",        icon: "ri-user-line",            desc: "1인칭 시점" },
];
const cameraMovePresets = [
  { id: "static",    label: "스태틱",   icon: "ri-stop-circle-line",    desc: "고정 카메라" },
  { id: "dolly-in",  label: "달리 인",  icon: "ri-zoom-in-line",        desc: "피사체로 천천히 접근" },
  { id: "dolly-out", label: "달리 아웃", icon: "ri-zoom-out-line",       desc: "피사체에서 멀어짐" },
  { id: "pan",       label: "패닝",     icon: "ri-arrow-left-right-line", desc: "좌우로 카메라 이동" },
  { id: "tilt",      label: "틸트",     icon: "ri-arrow-up-down-line",  desc: "상하로 카메라 이동" },
  { id: "orbit",     label: "오빗",     icon: "ri-refresh-line",        desc: "피사체 주위를 회전" },
  { id: "handheld",  label: "핸드헬드", icon: "ri-shake-hands-line",    desc: "손으로 든 듯한 흔들림" },
  { id: "crane",     label: "크레인",   icon: "ri-arrow-up-circle-line", desc: "위로 솟아오르는 샷" },
];
const lensPresets = [
  { id: "wide",      label: "광각",   icon: "ri-fullscreen-line",    desc: "넓은 화각, 공간감 강조" },
  { id: "normal",    label: "표준",   icon: "ri-camera-lens-line",   desc: "자연스러운 원근감" },
  { id: "telephoto", label: "망원",   icon: "ri-focus-3-line",       desc: "배경 압축, 인물 강조" },
  { id: "macro",     label: "매크로", icon: "ri-search-eye-line",    desc: "극단적 클로즈업" },
  { id: "fisheye",   label: "어안",   icon: "ri-bubble-chart-line",  desc: "왜곡된 초광각" },
];
const shotSizePresets = [
  { id: "extreme-wide",  label: "익스트림 와이드",    icon: "ri-landscape-line", desc: "배경 중심, 인물 작게" },
  { id: "wide",          label: "와이드 샷",          icon: "ri-image-line",     desc: "전신 + 배경" },
  { id: "medium",        label: "미디엄 샷",          icon: "ri-user-3-line",    desc: "허리 위" },
  { id: "close-up",      label: "클로즈업",           icon: "ri-emotion-line",   desc: "얼굴 중심" },
  { id: "extreme-close", label: "익스트림 클로즈업",  icon: "ri-eye-close-line", desc: "눈, 입술 등 디테일" },
];
const lightingPresets = [
  { id: "golden-hour", label: "골든 아워",   icon: "ri-sun-line",           color: "#ffd60a" },
  { id: "blue-hour",   label: "블루 아워",   icon: "ri-moon-line",          color: "#4fc3f7" },
  { id: "neon",        label: "네온 라이트", icon: "ri-flashlight-fill",    color: "#f72585" },
  { id: "studio",      label: "스튜디오",    icon: "ri-lightbulb-flash-line", color: "#ffffff" },
  { id: "candlelight", label: "캔들라이트",  icon: "ri-fire-line",          color: "#ff8c42" },
  { id: "moonlight",   label: "달빛",        icon: "ri-moon-foggy-line",    color: "#c77dff" },
  { id: "backlight",   label: "역광",        icon: "ri-contrast-line",      color: "#ffd60a" },
  { id: "dramatic",    label: "드라마틱",    icon: "ri-flashlight-line",    color: "#e84545" },
];
const weatherPresets = [
  { id: "clear",  label: "맑음", icon: "ri-sun-line" },
  { id: "cloudy", label: "흐림", icon: "ri-cloudy-line" },
  { id: "rain",   label: "비",   icon: "ri-rainy-line" },
  { id: "snow",   label: "눈",   icon: "ri-snowy-line" },
  { id: "fog",    label: "안개", icon: "ri-mist-line" },
  { id: "storm",  label: "폭풍", icon: "ri-thunderstorms-line" },
];
const timePresets = [
  { id: "dawn",    label: "새벽", icon: "ri-sun-foggy-line" },
  { id: "morning", label: "아침", icon: "ri-sun-line" },
  { id: "noon",    label: "낮",   icon: "ri-sun-fill" },
  { id: "sunset",  label: "노을", icon: "ri-sun-foggy-line" },
  { id: "night",   label: "밤",   icon: "ri-moon-line" },
  { id: "midnight",label: "자정", icon: "ri-moon-fill" },
];

const presetToText: Record<string, string> = {
  "eye-level": "eye-level shot", "low-angle": "low angle shot, looking up",
  "high-angle": "high angle shot, looking down", "birds-eye": "bird\'s eye view, top-down shot",
  "dutch-angle": "dutch angle, tilted camera", "pov": "POV shot, first person perspective",
  "static": "static camera", "dolly-in": "dolly in, slow push toward subject",
  "dolly-out": "dolly out, pulling back from subject", "pan": "panning shot, horizontal camera movement",
  "tilt": "tilt shot, vertical camera movement", "orbit": "orbiting shot, camera circling subject",
  "handheld": "handheld camera, slight shake", "crane": "crane shot, rising upward",
  "wide": "wide angle lens, expansive field of view", "normal": "standard lens, natural perspective",
  "telephoto": "telephoto lens, compressed background", "macro": "macro lens, extreme close-up detail",
  "fisheye": "fisheye lens, distorted ultra-wide",
  "extreme-wide": "extreme wide shot, establishing shot", "medium": "medium shot, waist up",
  "close-up": "close-up shot, face detail", "extreme-close": "extreme close-up, detail shot",
  "golden-hour": "golden hour lighting, warm sunlight", "blue-hour": "blue hour, twilight atmosphere",
  "neon": "neon lighting, vibrant colored lights", "studio": "studio lighting, professional setup",
  "candlelight": "candlelight, warm flickering glow", "moonlight": "moonlight, soft silver illumination",
  "backlight": "backlit, silhouette effect", "dramatic": "dramatic lighting, high contrast shadows",
  "clear": "clear sky, sunny weather", "cloudy": "overcast sky, cloudy",
  "rain": "rain, wet surfaces, raindrops", "snow": "snowfall, winter atmosphere",
  "fog": "foggy, misty atmosphere", "storm": "stormy weather, dramatic clouds",
  "dawn": "dawn, early morning light", "morning": "morning, fresh daylight",
  "noon": "midday, bright sunlight", "sunset": "sunset, orange and pink sky",
  "night": "nighttime, city lights", "midnight": "midnight, deep darkness",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getPresetLabel(key: keyof QuestPresets, value: string): string {
  const allLists = [...cameraAnglePresets, ...cameraMovePresets, ...lensPresets,
    ...shotSizePresets, ...lightingPresets, ...weatherPresets, ...timePresets];
  return allLists.find(p => p.id === value)?.label ?? value;
}

function countActivePresets(p: QuestPresets): number {
  return Object.values(p).filter(Boolean).length;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function PromptBuilder({
  quest, artist, accentColor, prompt, onPromptChange,
  negativePrompt, onNegativePromptChange, selectedTool, onToolChange,
  onIPImagesChange, onTokenSpend,
}: PromptBuilderProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const qp = quest.presets ?? {};

  // customized = user has unlocked and changed something
  const [isCustomMode, setIsCustomMode] = useState(false);

  // Preset selections — initialized from quest.presets
  const [selAngle,    setSelAngle]    = useState(qp.cameraAngle ?? "");
  const [selMove,     setSelMove]     = useState(qp.cameraMove  ?? "");
  const [selLens,     setSelLens]     = useState(qp.lens        ?? "");
  const [selShot,     setSelShot]     = useState(qp.shotSize    ?? "");
  const [selLighting, setSelLighting] = useState(qp.lighting    ?? "");
  const [selWeather,  setSelWeather]  = useState(qp.weather     ?? "");
  const [selTime,     setSelTime]     = useState(qp.timeOfDay   ?? "");

  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; preview: string; type: "image" | "video" }[]>([]);
  const [isDragging, setIsDragging]       = useState(false);
  const [activeTab, setActiveTab]         = useState<"tool" | "camera" | "scene">("tool");
  const [ipGenOpen, setIpGenOpen]         = useState(false);
  const [generatedIPImages, setGeneratedIPImages] = useState<GeneratedIPImage[]>([]);
  const ipImageListRef = useRef<HTMLDivElement>(null);
  const ipTagSectionRef = useRef<HTMLDivElement>(null);

  // Sync tool from quest preset on mount
  useEffect(() => {
    if (qp.tool) onToolChange(qp.tool);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Reset to quest defaults ──────────────────────────────────────────────
  const handleReset = () => {
    setSelAngle(qp.cameraAngle ?? "");
    setSelMove(qp.cameraMove   ?? "");
    setSelLens(qp.lens         ?? "");
    setSelShot(qp.shotSize     ?? "");
    setSelLighting(qp.lighting ?? "");
    setSelWeather(qp.weather   ?? "");
    setSelTime(qp.timeOfDay    ?? "");
    if (qp.tool) onToolChange(qp.tool);
    setIsCustomMode(false);
  };

  // ── IP tag insert ────────────────────────────────────────────────────────
  const insertIPTag = (ipIndex: number) => {
    const tag = `@IP${ipIndex + 1}`;
    const ta = textareaRef.current;
    if (!ta) { onPromptChange(prompt + (prompt ? " " : "") + tag); return; }
    const start = ta.selectionStart;
    const before = prompt.slice(0, start);
    const after  = prompt.slice(ta.selectionEnd);
    const sep    = before && !before.endsWith(" ") ? " " : "";
    onPromptChange(before + sep + tag + " " + after);
    setTimeout(() => {
      const pos = start + sep.length + tag.length + 1;
      ta.setSelectionRange(pos, pos);
      ta.focus();
    }, 0);
  };

  // ── Preset toggle (with prompt sync) ────────────────────────────────────
  const togglePreset = (id: string, current: string, setter: (v: string) => void) => {
    if (!isCustomMode) return; // locked
    const text = presetToText[id] || id;
    if (current === id) {
      setter("");
      onPromptChange(prompt.replace(`, ${text}`, "").replace(`${text}, `, "").replace(text, "").trim());
    } else {
      if (current) {
        const old = presetToText[current] || current;
        const cleaned = prompt.replace(`, ${old}`, "").replace(`${old}, `, "").replace(old, "").trim();
        setter(id);
        onPromptChange(cleaned + (cleaned && !cleaned.endsWith(",") ? ", " : "") + text);
      } else {
        setter(id);
        onPromptChange(prompt + (prompt && !prompt.endsWith(",") ? ", " : "") + text);
      }
    }
  };

  // ── File upload ──────────────────────────────────────────────────────────
  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach((file) => {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");
      if (!isImage && !isVideo) return;
      const reader = new FileReader();
      reader.onload = (e) => setUploadedFiles(prev => [
        ...prev, { name: file.name, preview: e.target?.result as string, type: isImage ? "image" : "video" },
      ]);
      reader.readAsDataURL(file);
    });
  }, []);

  // ── Preset chip ──────────────────────────────────────────────────────────
  const PresetChip = ({ id, label, icon, desc, selected, onToggle, color, isDefault }: {
    id: string; label: string; icon: string; desc?: string; selected: boolean;
    onToggle: () => void; color?: string; isDefault?: boolean;
  }) => (
    <button
      onClick={onToggle}
      title={desc}
      disabled={!isCustomMode}
      className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all duration-150 whitespace-nowrap"
      style={
        selected
          ? { borderColor: `${accentColor}70`, background: `${accentColor}18`, color: accentColor,
              cursor: isCustomMode ? "pointer" : "default" }
          : { borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)",
              color: isCustomMode ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.25)",
              cursor: isCustomMode ? "pointer" : "default" }
      }
    >
      <i className={`${icon} text-sm`} style={selected && color ? { color } : {}} />
      {label}
      {/* default badge */}
      {isDefault && (
        <span className="ml-0.5 px-1 py-px rounded text-[9px] font-bold leading-none"
          style={{ background: `${accentColor}30`, color: accentColor }}>
          권장
        </span>
      )}
    </button>
  );

  const hasPresets = Object.keys(qp).length > 0;
  const presetCount = countActivePresets(qp);

  return (
    <div className="space-y-5">

      {/* ── IP 태그 삽입 ─────────────────────────────────────────────────── */}
      <div ref={ipTagSectionRef} className="rounded-xl border border-white/10 bg-white/3 p-4">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-at-line text-sm" style={{ color: accentColor }} />
          <span className="text-white/70 text-xs font-semibold">IP 태그 삽입</span>
          <span className="text-white/30 text-xs">— 클릭해서 프롬프트에 추가</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {artist.ipImages.map((img, i) => (
            <button key={i} onClick={() => insertIPTag(i)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:border-white/25 hover:bg-white/8 transition-all cursor-pointer group">
              <div className="w-6 h-6 rounded-md overflow-hidden flex-shrink-0">
                <img src={img} alt={`IP ${i + 1}`} className="w-full h-full object-cover object-top" />
              </div>
              <span className="text-white/60 text-xs font-mono group-hover:text-white transition-colors">@IP{i + 1}</span>
              <i className="ri-add-line text-white/30 text-xs group-hover:text-white/60 transition-colors" />
            </button>
          ))}
        </div>
        <p className="text-white/25 text-[11px] mt-2.5">
          예시: <span className="text-white/40 font-mono">@IP1이 @IP2와 함께 바다로 뛰어드는 장면, 슬로우 모션</span>
        </p>
      </div>

      {/* ── IP 이미지 생성 ───────────────────────────────────────────────── */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: ipGenOpen ? `${accentColor}40` : "rgba(255,255,255,0.08)" }}>
        {/* Header toggle */}
        <button
          onClick={() => setIpGenOpen(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 cursor-pointer transition-all"
          style={{ background: ipGenOpen ? `${accentColor}08` : "rgba(255,255,255,0.02)" }}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 flex items-center justify-center rounded-md flex-shrink-0" style={{ background: `${accentColor}20` }}>
              <i className="ri-image-ai-line text-xs" style={{ color: accentColor }} />
            </div>
            <div className="text-left">
              <p className="text-xs font-semibold" style={{ color: accentColor }}>IP 이미지 생성</p>
              <p className="text-[11px] text-white/30 mt-px">
                {generatedIPImages.length > 0
                  ? `${generatedIPImages.length}개 이미지 생성됨 · 영상 참조로 활용`
                  : "의상·배경 변경 후 나만의 IP 이미지 생성 (토큰 소모)"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {generatedIPImages.length > 0 && (
              <div className="flex -space-x-1.5">
                {generatedIPImages.slice(0, 3).map(img => (
                  <div key={img.id} className="w-6 h-6 rounded-full overflow-hidden border-2 border-[#0a0a0f]">
                    <img src={img.previewUrl} alt={img.ipTag} className="w-full h-full object-cover object-top" />
                  </div>
                ))}
              </div>
            )}
            <i className={`${ipGenOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-white/30 text-sm`} />
          </div>
        </button>

        {ipGenOpen && (
          <div className="border-t border-white/8 p-4">
            <IPImageGenerator
              quest={quest}
              artist={artist}
              accentColor={accentColor}
              onComplete={(images) => {
                setGeneratedIPImages(images);
                if (onIPImagesChange) onIPImagesChange(images);
              }}
              onTokenSpend={(amount) => {
                if (onTokenSpend) onTokenSpend(amount);
              }}
              onImageGenerated={(newImg) => {
                setGeneratedIPImages(prev => {
                  const filtered = prev.filter(g => g.ipIndex !== newImg.ipIndex);
                  const updated = [...filtered, newImg];
                  if (onIPImagesChange) onIPImagesChange(updated);
                  return updated;
                });
              }}
            />
            {/* 하단 접기 버튼 */}
            <button
              onClick={() => {
                setIpGenOpen(false);
                setTimeout(() => {
                  ipTagSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 100);
              }}
              className="mt-4 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-white/8 text-white/30 text-xs hover:text-white/50 hover:border-white/15 transition-all cursor-pointer"
            >
              <i className="ri-arrow-up-s-line text-sm" />
              접기
            </button>
          </div>
        )}

      </div>

      {/* ── 생성된 IP 이미지 → 프롬프트 삽입 리스트 ─────────────────────── */}
      {generatedIPImages.length > 0 && (
        <div ref={ipImageListRef} className="rounded-xl border overflow-hidden" style={{ borderColor: `${accentColor}25` }}>
          {/* 헤더 */}
          <div className="flex items-center justify-between px-4 py-2.5" style={{ background: `${accentColor}08` }}>
            <div className="flex items-center gap-2">
              <i className="ri-image-ai-line text-xs" style={{ color: accentColor }} />
              <span className="text-xs font-semibold" style={{ color: accentColor }}>생성된 IP 이미지</span>
              <span className="text-[11px] text-white/30">— 클릭해서 프롬프트에 삽입</span>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
              style={{ background: `${accentColor}20`, color: accentColor }}>
              {generatedIPImages.length}개
            </span>
          </div>
          {/* 가로 스크롤 카드 리스트 */}
          <div className="flex gap-2 px-3 py-3 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {generatedIPImages.map((img) => {
              const isInserted = prompt.includes(img.ipTag);
              const insertText = `${img.ipTag}(${img.outfit} 의상, ${img.background} 배경)`;
              return (
                <button
                  key={img.id}
                  onClick={() => {
                    const ta = textareaRef.current;
                    if (!ta) {
                      onPromptChange(prompt + (prompt ? " " : "") + insertText);
                    } else {
                      const start = ta.selectionStart;
                      const before = prompt.slice(0, start);
                      const after  = prompt.slice(ta.selectionEnd);
                      const sep    = before && !before.endsWith(" ") ? " " : "";
                      onPromptChange(before + sep + insertText + " " + after);
                      setTimeout(() => {
                        const pos = start + sep.length + insertText.length + 1;
                        ta.setSelectionRange(pos, pos);
                        ta.focus();
                      }, 0);
                    }
                  }}
                  className="flex-shrink-0 flex flex-col items-center gap-1.5 p-2 rounded-xl border transition-all cursor-pointer group w-[88px]"
                  style={isInserted
                    ? { borderColor: `${accentColor}60`, background: `${accentColor}12` }
                    : { borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }
                  }
                >
                  {/* 이미지 */}
                  <div className="relative w-full rounded-lg overflow-hidden border border-white/10" style={{ aspectRatio: "1/1" }}>
                    <img src={img.previewUrl} alt={img.ipTag} className="w-full h-full object-cover object-top" />
                    {isInserted && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-lg"
                        style={{ background: `${accentColor}40` }}>
                        <i className="ri-check-double-line text-white text-base" />
                      </div>
                    )}
                  </div>
                  {/* 태그 */}
                  <span className="text-[10px] font-bold font-mono leading-none"
                    style={{ color: isInserted ? accentColor : "rgba(255,255,255,0.5)" }}>
                    {img.ipTag}
                  </span>
                  {/* 의상 */}
                  <span className="text-[9px] text-white/30 truncate w-full text-center leading-none">
                    {img.outfit}
                  </span>
                  {/* 삽입/삽입됨 */}
                  <span className="text-[9px] font-semibold leading-none"
                    style={{ color: isInserted ? accentColor : "rgba(255,255,255,0.25)" }}>
                    {isInserted ? "✓ 삽입됨" : "+ 삽입"}
                  </span>
                </button>
              );
            })}
          </div>
          {/* 삽입 텍스트 미리보기 */}
          <div className="px-4 pb-3">
            <p className="text-[10px] text-white/20 font-mono leading-relaxed">
              예: <span className="text-white/35">{generatedIPImages[0]?.ipTag}({generatedIPImages[0]?.outfit} 의상, {generatedIPImages[0]?.background} 배경)</span>
            </p>
          </div>
        </div>
      )}

      {/* ── 프롬프트 텍스트 ───────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-white/60 text-xs font-semibold">프롬프트 *</label>
          <span className="text-white/20 text-xs">{prompt.length}/500</span>
        </div>
        <div className="relative">
          <textarea ref={textareaRef} value={prompt} onChange={e => onPromptChange(e.target.value)}
            placeholder="예: @IP1이 @IP2와 함께 바다로 뛰어드는 장면, 슬로우 모션, 황금빛 노을..."
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-white/30 transition-colors resize-none leading-relaxed"
            rows={4} maxLength={500} />
          {prompt.includes("@IP") && (
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-white/5 border border-white/10">
              <span className="text-white/30 text-[10px]">{(prompt.match(/@IP\d+/g) || []).length}개 IP 태그</span>
            </div>
          )}
        </div>
      </div>

      {/* ── 나의 콘텐츠 업로드 ───────────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="text-white/60 text-xs font-semibold">나의 콘텐츠 업로드</label>
          <span className="text-white/30 text-xs">— 참고 이미지/영상 (선택)</span>
        </div>
        <div onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={e => { e.preventDefault(); setIsDragging(false); handleFiles(e.dataTransfer.files); }}
          className="rounded-xl border-2 border-dashed transition-all duration-200 p-4"
          style={isDragging
            ? { borderColor: `${accentColor}60`, background: `${accentColor}08` }
            : { borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
          {uploadedFiles.length === 0 ? (
            <label className="flex flex-col items-center gap-2 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: `${accentColor}15` }}>
                <i className="ri-upload-cloud-2-line text-xl" style={{ color: accentColor }} />
              </div>
              <div className="text-center">
                <p className="text-white/50 text-xs font-medium">드래그하거나 클릭해서 업로드</p>
                <p className="text-white/25 text-[11px] mt-0.5">이미지 (JPG, PNG, WEBP) · 영상 (MP4, MOV) · 최대 50MB</p>
              </div>
              <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={e => handleFiles(e.target.files)} />
            </label>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {uploadedFiles.map((f, i) => (
                  <div key={i} className="relative group">
                    <div className="w-20 h-20 rounded-lg overflow-hidden border border-white/10 bg-white/5">
                      {f.type === "image"
                        ? <img src={f.preview} alt={f.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                            <i className="ri-video-line text-xl text-white/40" />
                            <span className="text-white/30 text-[9px] px-1 text-center truncate w-full">{f.name}</span>
                          </div>
                      }
                    </div>
                    <button onClick={() => setUploadedFiles(p => p.filter((_, j) => j !== i))}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/80 border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                      <i className="ri-close-line text-white text-xs" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded-lg border border-dashed border-white/15 flex items-center justify-center cursor-pointer hover:border-white/30 transition-colors">
                  <i className="ri-add-line text-white/30 text-xl" />
                  <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={e => handleFiles(e.target.files)} />
                </label>
              </div>
              <p className="text-white/30 text-[11px]">{uploadedFiles.length}개 파일 업로드됨 · AI가 스타일 참고로 활용합니다</p>
            </div>
          )}
        </div>
      </div>

      {/* ── 프리셋 & AI 도구 / 카메라 / 씬 탭 (통합) ───────────────────── */}
      <div className="rounded-xl border overflow-hidden transition-all duration-200"
        style={{ borderColor: isCustomMode ? "rgba(255,255,255,0.12)" : `${accentColor}35` }}>

        {/* ── 헤더: 항상 표시 ── */}
        <div className="flex items-center justify-between px-4 py-3"
          style={{ background: isCustomMode ? "rgba(255,255,255,0.02)" : `${accentColor}08` }}>
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 flex items-center justify-center rounded-md flex-shrink-0"
              style={{ background: isCustomMode ? "rgba(255,255,255,0.08)" : `${accentColor}20` }}>
              <i className="ri-shield-star-line text-xs"
                style={{ color: isCustomMode ? "rgba(255,255,255,0.4)" : accentColor }} />
            </div>
            <div>
              <p className="text-xs font-semibold"
                style={{ color: isCustomMode ? "rgba(255,255,255,0.45)" : accentColor }}>
                {hasPresets ? `${artist.name} 권장 프리셋` : "AI 도구 · 카메라 · 씬 설정"}
              </p>
              <p className="text-[11px] mt-px text-white/30">
                {isCustomMode
                  ? "커스텀 모드 — 자유롭게 조정하세요"
                  : hasPresets
                    ? `${presetCount}개 설정 적용됨 · 커스텀 버튼으로 수정 가능`
                    : "AI 도구, 카메라 기법, 씬 분위기를 선택하세요"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isCustomMode && hasPresets && (
              <button onClick={handleReset}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/10 text-white/40 text-xs hover:text-white/70 hover:border-white/20 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-restart-line text-xs" />
                초기화
              </button>
            )}
            <button
              onClick={() => setIsCustomMode(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
              style={isCustomMode
                ? { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.12)" }
                : { background: `${accentColor}20`, color: accentColor, border: `1px solid ${accentColor}40` }
              }
            >
              <i className={`${isCustomMode ? "ri-lock-unlock-line" : "ri-lock-line"} text-xs`} />
              {isCustomMode ? "잠금" : "커스텀"}
            </button>
          </div>
        </div>

        {/* ── 프리셋 요약 pills: 항상 표시 ── */}
        {hasPresets && (
          <div className="px-4 pb-3 pt-1 flex flex-wrap gap-1.5 border-b border-white/5">
            {qp.tool && (() => {
              const t = aiTools.find(x => x.id === qp.tool);
              return t ? (
                <span key="tool" className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px]"
                  style={{ background: `${t.color}15`, color: t.color, border: `1px solid ${t.color}30` }}>
                  <i className={`${t.icon} text-xs`} />{t.name}
                </span>
              ) : null;
            })()}
            {([
              ["shotSize", selShot], ["cameraAngle", selAngle], ["cameraMove", selMove],
              ["lens", selLens], ["lighting", selLighting], ["timeOfDay", selTime], ["weather", selWeather],
            ] as [keyof QuestPresets, string][]).map(([key, val]) => val ? (
              <span key={key} className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px]"
                style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.1)" }}>
                {getPresetLabel(key, val)}
                {isCustomMode && val !== (qp as Record<string, string | undefined>)[key] && (
                  <span className="text-[9px] font-bold" style={{ color: accentColor }}>수정됨</span>
                )}
              </span>
            ) : null)}
          </div>
        )}

        {/* ── 탭 패널: 커스텀 모드일 때만 펼침 ── */}
        {isCustomMode && (
          <>
            {/* Tab bar */}
            <div className="flex border-b border-white/8">
              {(["tool", "camera", "scene"] as const).map(tab => {
                const meta = {
                  tool:   { icon: "ri-magic-line",     label: "AI 도구" },
                  camera: { icon: "ri-camera-3-line",  label: "카메라 기법" },
                  scene:  { icon: "ri-landscape-line", label: "씬 분위기" },
                }[tab];
                return (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className="flex-1 py-3 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
                    style={activeTab === tab
                      ? { color: accentColor, borderBottom: `2px solid ${accentColor}`, background: `${accentColor}08` }
                      : { color: "rgba(255,255,255,0.35)", borderBottom: "2px solid transparent" }}>
                    <i className={`${meta.icon} mr-1.5`} />{meta.label}
                  </button>
                );
              })}
            </div>

            <div className="p-4 space-y-4">

              {/* ── AI 도구 탭 ── */}
              {activeTab === "tool" && (
                <div className="grid grid-cols-1 gap-2">
                  {aiTools.map(tool => {
                    const isSelected = selectedTool === tool.id;
                    const isDefault  = qp.tool === tool.id;
                    return (
                      <button key={tool.id}
                        onClick={() => onToolChange(tool.id)}
                        className="flex items-center gap-3 p-3 rounded-xl border text-left transition-all duration-150 cursor-pointer"
                        style={isSelected
                          ? { borderColor: `${tool.color}60`, background: `${tool.color}12` }
                          : { borderColor: "rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }
                        }>
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                          style={{ background: `${tool.color}20`, border: `1px solid ${tool.color}40` }}>
                          <i className={`${tool.icon} text-sm`} style={{ color: tool.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-semibold text-xs">{tool.name}</span>
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: `${tool.color}20`, color: tool.color }}>{tool.badge}</span>
                            {isDefault && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: `${accentColor}25`, color: accentColor }}>권장</span>}
                          </div>
                          <p className="text-white/35 text-[11px] mt-0.5 truncate">{tool.desc}</p>
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: tool.color }}>
                            <i className="ri-check-line text-white text-xs" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* ── 카메라 기법 탭 ── */}
              {activeTab === "camera" && (
                <>
                  <div>
                    <p className="text-white/40 text-[11px] font-semibold uppercase tracking-wider mb-2">샷 사이즈</p>
                    <div className="flex flex-wrap gap-1.5">
                      {shotSizePresets.map(p => (
                        <PresetChip key={p.id} id={p.id} label={p.label} icon={p.icon} desc={p.desc}
                          selected={selShot === p.id} isDefault={qp.shotSize === p.id}
                          onToggle={() => togglePreset(p.id, selShot, setSelShot)} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-white/40 text-[11px] font-semibold uppercase tracking-wider mb-2">카메라 앵글</p>
                    <div className="flex flex-wrap gap-1.5">
                      {cameraAnglePresets.map(p => (
                        <PresetChip key={p.id} id={p.id} label={p.label} icon={p.icon} desc={p.desc}
                          selected={selAngle === p.id} isDefault={qp.cameraAngle === p.id}
                          onToggle={() => togglePreset(p.id, selAngle, setSelAngle)} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-white/40 text-[11px] font-semibold uppercase tracking-wider mb-2">카메라 무브먼트</p>
                    <div className="flex flex-wrap gap-1.5">
                      {cameraMovePresets.map(p => (
                        <PresetChip key={p.id} id={p.id} label={p.label} icon={p.icon} desc={p.desc}
                          selected={selMove === p.id} isDefault={qp.cameraMove === p.id}
                          onToggle={() => togglePreset(p.id, selMove, setSelMove)} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-white/40 text-[11px] font-semibold uppercase tracking-wider mb-2">렌즈 타입</p>
                    <div className="flex flex-wrap gap-1.5">
                      {lensPresets.map(p => (
                        <PresetChip key={p.id} id={p.id} label={p.label} icon={p.icon} desc={p.desc}
                          selected={selLens === p.id} isDefault={qp.lens === p.id}
                          onToggle={() => togglePreset(p.id, selLens, setSelLens)} />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ── 씬 분위기 탭 ── */}
              {activeTab === "scene" && (
                <>
                  <div>
                    <p className="text-white/40 text-[11px] font-semibold uppercase tracking-wider mb-2">조명</p>
                    <div className="flex flex-wrap gap-1.5">
                      {lightingPresets.map(p => (
                        <PresetChip key={p.id} id={p.id} label={p.label} icon={p.icon} color={p.color}
                          selected={selLighting === p.id} isDefault={qp.lighting === p.id}
                          onToggle={() => togglePreset(p.id, selLighting, setSelLighting)} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-white/40 text-[11px] font-semibold uppercase tracking-wider mb-2">시간대</p>
                    <div className="flex flex-wrap gap-1.5">
                      {timePresets.map(p => (
                        <PresetChip key={p.id} id={p.id} label={p.label} icon={p.icon}
                          selected={selTime === p.id} isDefault={qp.timeOfDay === p.id}
                          onToggle={() => togglePreset(p.id, selTime, setSelTime)} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-white/40 text-[11px] font-semibold uppercase tracking-wider mb-2">날씨</p>
                    <div className="flex flex-wrap gap-1.5">
                      {weatherPresets.map(p => (
                        <PresetChip key={p.id} id={p.id} label={p.label} icon={p.icon}
                          selected={selWeather === p.id} isDefault={qp.weather === p.id}
                          onToggle={() => togglePreset(p.id, selWeather, setSelWeather)} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── 네거티브 프롬프트 ─────────────────────────────────────────────── */}
      <div>
        <label className="text-white/60 text-xs font-semibold mb-2 block">
          네거티브 프롬프트 <span className="text-white/25 font-normal">(선택)</span>
        </label>
        <textarea value={negativePrompt} onChange={e => onNegativePromptChange(e.target.value)}
          placeholder="제외할 요소를 입력하세요. 예: blurry, low quality, distorted face..."
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-white/30 transition-colors resize-none"
          rows={2} maxLength={200} />
      </div>

      {/* ── 프롬프트 미리보기 ─────────────────────────────────────────────── */}
      {prompt.trim() && (
        <div className="rounded-xl p-3.5" style={{ background: `${accentColor}06`, border: `1px solid ${accentColor}20` }}>
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-eye-line text-xs" style={{ color: accentColor }} />
            <span className="text-[11px] font-semibold" style={{ color: accentColor }}>완성된 프롬프트 미리보기</span>
          </div>
          <p className="text-white/50 text-xs leading-relaxed font-mono break-all">
            {prompt.split(/(@IP\d+)/g).map((part, i) =>
              /^@IP\d+$/.test(part)
                ? <span key={i} className="font-bold" style={{ color: accentColor }}>{part}</span>
                : <span key={i}>{part}</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
